import {
  CreativeImportJobStatus,
  CreativeImportProgress,
  CreativeImportValidationReport,
} from '@marketing-milk/interfaces'

export type CreativeImportStep = 'upload' | 'review' | 'options' | 'import' | 'summary'

export interface RowUIState {
  selected: boolean
  editedName: string
}

export interface CreativeImportState {
  step: CreativeImportStep
  file: File | null
  validating: boolean
  report: CreativeImportValidationReport | null
  /** Persisted import-job id from validate; null until a clean report comes back. */
  creativeImportID: number | null
  /** Per-row UI state keyed by rowNumber. */
  rows: Record<number, RowUIState>
  /** Object URLs for row thumbnails keyed by rowNumber. Revoked on reset/unmount. */
  thumbnails: Record<number, string>
  /** Optional existing sprint to attach imported drafts to (Options step). */
  sprintID: number | null
  confirming: boolean
  /** True once the backend accepted the confirm (incl. a 409 "already confirmed"). Gates progress polling. */
  confirmed: boolean
  confirmError: string | null
  progress: CreativeImportProgress | null
  error: string | null
}

export type CreativeImportAction =
  | { type: 'validateStart'; file: File }
  | { type: 'validateSuccess'; report: CreativeImportValidationReport }
  | { type: 'validateError'; message: string }
  | { type: 'thumbnailsLoaded'; thumbnails: Record<number, string> }
  | { type: 'toggleRow'; rowNumber: number }
  | { type: 'editName'; rowNumber: number; name: string }
  | { type: 'goToOptions' }
  | { type: 'goToImport' }
  | { type: 'backToReview' }
  | { type: 'setSprint'; sprintID: number | null }
  | { type: 'confirmStart' }
  | { type: 'confirmSuccess' }
  | { type: 'confirmError'; message: string }
  | { type: 'progressUpdated'; progress: CreativeImportProgress }
  | { type: 'reset' }

export const initialCreativeImportState: CreativeImportState = {
  step: 'upload',
  file: null,
  validating: false,
  report: null,
  creativeImportID: null,
  rows: {},
  thumbnails: {},
  sprintID: null,
  confirming: false,
  confirmed: false,
  confirmError: null,
  progress: null,
  error: null,
}

const TERMINAL_JOB_STATUSES: CreativeImportJobStatus[] = [
  CreativeImportJobStatus.Completed,
  CreativeImportJobStatus.CompletedWithErrors,
  CreativeImportJobStatus.Failed,
]

export function isTerminalJobStatus(status: CreativeImportJobStatus): boolean {
  return TERMINAL_JOB_STATUSES.includes(status)
}

/** Selectable rows start selected (warnings included); blocked rows stay unselected. */
export function seedRowState(report: CreativeImportValidationReport): Record<number, RowUIState> {
  return Object.fromEntries(
    report.rows.map(row => [
      row.rowNumber,
      { selected: row.selectable, editedName: row.effectiveName },
    ])
  )
}

export function creativeImportReducer(
  state: CreativeImportState,
  action: CreativeImportAction
): CreativeImportState {
  switch (action.type) {
    case 'validateStart':
      return { ...initialCreativeImportState, file: action.file, validating: true }
    case 'validateSuccess':
      return {
        ...state,
        validating: false,
        report: action.report,
        creativeImportID: action.report.creativeImportID,
        rows: seedRowState(action.report),
        // Archive-level rejection keeps the user on Upload with the errors shown.
        step: action.report.archiveErrors.length ? 'upload' : 'review',
      }
    case 'validateError':
      return { ...state, validating: false, error: action.message }
    case 'thumbnailsLoaded':
      return { ...state, thumbnails: action.thumbnails }
    case 'toggleRow': {
      const row = state.rows[action.rowNumber]
      if (!row) return state
      return {
        ...state,
        rows: { ...state.rows, [action.rowNumber]: { ...row, selected: !row.selected } },
      }
    }
    case 'editName': {
      const row = state.rows[action.rowNumber]
      if (!row) return state
      return {
        ...state,
        rows: { ...state.rows, [action.rowNumber]: { ...row, editedName: action.name } },
      }
    }
    case 'goToOptions':
      return { ...state, step: 'options' }
    case 'goToImport':
      return { ...state, step: 'import' }
    case 'backToReview':
      return { ...state, step: 'review', confirmError: null }
    case 'setSprint':
      return { ...state, sprintID: action.sprintID }
    case 'confirmStart':
      return { ...state, confirming: true, confirmError: null }
    case 'confirmSuccess':
      return { ...state, confirming: false, confirmed: true }
    case 'confirmError':
      return { ...state, confirming: false, confirmError: action.message }
    case 'progressUpdated':
      return {
        ...state,
        progress: action.progress,
        // The Summary is an explicit step so closing/reopening state is testable.
        step: isTerminalJobStatus(action.progress.status) ? 'summary' : state.step,
      }
    case 'reset':
      return initialCreativeImportState
    default:
      return state
  }
}
